import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { AGENTS, DEPARTMENTS } from '../../utils/agents';
import { useProjectStore } from '../../stores/projectStore';

interface CreateProjectModalProps {
  onClose: () => void;
}

export default function CreateProjectModal({ onClose }: CreateProjectModalProps) {
  const createProject = useProjectStore((s) => s.createProject);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [department, setDepartment] = useState('');
  const [leadAgent, setLeadAgent] = useState('');
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  function handleAgentChange(slug: string) {
    setLeadAgent(slug);
    if (slug) {
      const agent = AGENTS.find((a) => a.slug === slug);
      if (agent) {
        setDepartment(agent.department);
      }
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await createProject({
        name: name.trim(),
        description: description.trim() || null,
        department: department || null,
        lead_agent: leadAgent || null,
        start_date: startDate || null,
        target_date: targetDate || null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-bg-secondary border border-border-subtle rounded-xl p-6 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-text-primary">New Project</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              placeholder="Project name"
              autoFocus
            />
            {error ? <p className="text-error text-xs mt-1">{error}</p> : null}
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple resize-none"
              placeholder="Optional description"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">None</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Lead Agent</label>
              <select
                value={leadAgent}
                onChange={(e) => handleAgentChange(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">None</option>
                {AGENTS.map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.name} — {a.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              />
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Target Date</label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <button
              type="button"
              onClick={handleClose}
              className="text-text-muted hover:text-text-secondary text-sm transition-colors px-3 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
